import { compose, withState, withHandlers, withPropsOnChange, withProps } from 'recompose';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { propOr, pipe, head, last, find } from 'ramda';

import AddTimeTrackingEntry from '../components/AddTimeTrackingEntry';
import withNotifications from '../../../common/withNotifications';
import { addBarberTrackTime } from '../actions';
import withTimezone from '../../../auth/utils/withTimezone';
import config from '../../../../config';
import withToggle from '../../../common/hoc/withToggle';
import { getShop } from '../../../auth/selectors';
import { getBarberAllowedTimeTrackRoles, getById as getBarberById } from '../../../main/barbers/selector';
import withExtendedTimeTracking from '../hoc/withExtendedTimeTracking';
import { DEFAULT_TRACKING_ROLE } from '../../constants';
import { t } from '../../../../modules/infra/i18n';

const getMomentDay = props => moment.tz(props.currentDate, config.dateFormatISO, props.timezone);

const getTimes = (props) => {
  const day = getMomentDay(props);
  const startedAt = moment(day).hours(props.startedAtHours).minutes(props.startedAtMinutes).toJSON();
  const endedAt = moment(day).hours(props.endedAtHours).minutes(props.endedAtMinutes).toJSON();
  return {
    startedAt,
    endedAt,
  };
};

const getWorkdays = pipe(propOr([], 'schedules'), head, propOr([], 'workdays'));
const findCurrentWorkday = (workdays, props) => {
  const day = getMomentDay(props);
  return find(workday => workday.weekDay === day.isoWeekday(), workdays);
};

const AddTimeTrackingEntryContainer = compose(
  withNotifications,
  withTimezone,
  withExtendedTimeTracking,
  withState('startedAtHours', 'setStartedAtHours', 0),
  withState('startedAtMinutes', 'setStartedAtMinutes', 0),
  withState('endedAtHours', 'setEndedAtHours', 0),
  withState('endedAtMinutes', 'setEndedAtMinutes', 0),
  withState('role', 'setRole', DEFAULT_TRACKING_ROLE),
  withState('passcode', 'setPasscode', ''),
  withState('valid', 'setValid', false),
  connect(
    (state, ownProps) => ({
      shopId: getShop(state),
      barber: getBarberById(state, ownProps.barberId),
      allowedTimeTrackRoles: getBarberAllowedTimeTrackRoles(state, ownProps.barberId),
    }),
    { addBarberTrackTime }
  ),
  withToggle(),
  withHandlers({
    closeModal: props => () => {
      props.off();
      props.setPasscode('');
      props.setRole(DEFAULT_TRACKING_ROLE);
    },
  }),
  withHandlers({
    onStartedAtHoursChange: props => (e) => {
      props.setStartedAtHours(e.target.value);
    },
    onStartedAtMinutesChange: props => (e) => {
      props.setStartedAtMinutes(e.target.value);
    },
    onEndedAtHoursChange: props => (e) => {
      props.setEndedAtHours(e.target.value);
    },
    onEndedAtMinutesChange: props => (e) => {
      props.setEndedAtMinutes(e.target.value);
    },
    onRoleChange: props => (e) => {
      props.setRole(e.target.value);
    },
    onPasscodeChange: props => (e) => {
      props.setPasscode(e.target.value);
    },
    onSubmit: props => (e) => {
      e.preventDefault();
      const { startedAt, endedAt } = getTimes(props);

      const payload = {
        startedAt,
        endedAt,
        shopId: props.shopId,
      };

      if (props.extendedHourlyWages) {
        payload.role = props.role;
      }

      if (props.requirePasscode) {
        payload.passcode = props.passcode;
      }

      props.addBarberTrackTime(
        props.barberId,
        payload
      )
        .then(() => {
          props.closeModal();
          props.addNotification({
            title: t('barber:common.success'),
            level: 'success',
            message: t('barber:time_tracking.add.success_message'),
          });
        });
    },
  }),
  withPropsOnChange(
    ['startedAtHours', 'startedAtMinutes', 'endedAtHours', 'endedAtMinutes', 'passcode'],
    (props) => {
      const { startedAt, endedAt } = getTimes(props);
      const isPasscodeValid = props.requirePasscode ? !!props.passcode : true;

      props.setValid(
        props.startedAtHours <= 23 && props.startedAtMinutes <= 59 &&
        props.endedAtHours <= 23 && props.endedAtMinutes <= 59 &&
        moment(startedAt).isBefore(endedAt) && isPasscodeValid
      );
    }
  ),
  withPropsOnChange(
    ['currentDate', 'entries'],
    (props) => {
      const workdays = getWorkdays(props.barber);
      const currentWorkday = findCurrentWorkday(workdays, props);

      if (currentWorkday) {
        props.setEndedAtHours(currentWorkday.finishHour);
        props.setEndedAtMinutes(currentWorkday.finishMinute);
        props.setStartedAtHours(currentWorkday.startHour);
        props.setStartedAtMinutes(currentWorkday.startMinute);
      }

      const lastEntry = last(props.entries);
      if (lastEntry && lastEntry.endedAt) {
        props.setStartedAtHours(moment.tz(lastEntry.endedAt, props.timezone).hours());
        props.setStartedAtMinutes(moment.tz(lastEntry.endedAt, props.timezone).minutes());
      }
    }
  ),
  withProps(props => ({
    isSameTimezoneWithUser: (
      moment().tz(moment.tz.guess()).format(config.timeFormat) === moment().tz(props.timezone).format(config.timeFormat)
    ),
    shopTime: moment().tz(props.timezone).format(config.timeFormat),
  }))
)(AddTimeTrackingEntry);

export default AddTimeTrackingEntryContainer;
